/**
 * System Control Context
 * 
 * Admin-only context for managing global system controls.
 * Controls lead intake at the system and vertical level.
 * 
 * In-memory only — no persistence, no backend.
 */

import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";

// --- Type Definitions ---

export interface SystemControlState {
  /** Global lead intake toggle */
  globalIntakeEnabled: boolean;
  
  /** Per-vertical intake toggles */
  verticalIntake: {
    life_insurance: boolean;
    mortgage: boolean;
  };
}

export interface SystemControlActions {
  /** Toggle global lead intake */
  setGlobalIntake: (enabled: boolean) => void;
  
  /** Toggle vertical-specific intake */
  setVerticalIntake: (vertical: "life_insurance" | "mortgage", enabled: boolean) => void;
  
  /** Check if intake is active for a specific vertical */
  isIntakeActive: (vertical: "life_insurance" | "mortgage") => boolean;
  
  /** Get rejection reason if intake is disabled */
  getIntakeRejectionReason: (vertical: "life_insurance" | "mortgage") => string | null;
}

// --- Initial State ---

const initialState: SystemControlState = {
  globalIntakeEnabled: true,
  verticalIntake: {
    life_insurance: true,
    mortgage: true,
  },
};

// --- Context ---

const SystemControlContext = createContext<{
  state: SystemControlState;
  actions: SystemControlActions;
} | undefined>(undefined);

// --- Provider ---

export const SystemControlProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<SystemControlState>(initialState);

  const setGlobalIntake = useCallback((enabled: boolean) => {
    setState((prev) => ({
      ...prev,
      globalIntakeEnabled: enabled,
    }));
  }, []);

  const setVerticalIntake = useCallback(
    (vertical: "life_insurance" | "mortgage", enabled: boolean) => {
      setState((prev) => ({
        ...prev,
        verticalIntake: {
          ...prev.verticalIntake,
          [vertical]: enabled,
        },
      }));
    },
    []
  );

  const isIntakeActive = useCallback(
    (vertical: "life_insurance" | "mortgage"): boolean => {
      // Global must be enabled AND vertical must be enabled
      return state.globalIntakeEnabled && state.verticalIntake[vertical];
    },
    [state.globalIntakeEnabled, state.verticalIntake]
  );

  const getIntakeRejectionReason = useCallback(
    (vertical: "life_insurance" | "mortgage"): string | null => {
      if (!state.globalIntakeEnabled) {
        return "Global lead intake is currently paused";
      }
      if (!state.verticalIntake[vertical]) {
        const verticalName = vertical === "life_insurance" ? "Life Insurance" : "Mortgage";
        return `${verticalName} lead intake is currently paused`;
      }
      return null;
    },
    [state.globalIntakeEnabled, state.verticalIntake]
  );

  const actions: SystemControlActions = {
    setGlobalIntake,
    setVerticalIntake,
    isIntakeActive,
    getIntakeRejectionReason,
  };

  return (
    <SystemControlContext.Provider value={{ state, actions }}>
      {children}
    </SystemControlContext.Provider>
  );
};

// --- Hook ---

export const useSystemControl = () => {
  const context = useContext(SystemControlContext);
  if (context === undefined) {
    throw new Error("useSystemControl must be used within a SystemControlProvider");
  }
  return context;
};

export default SystemControlContext;
